import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import FollowButton from "@/components/FollowButton";
import { Users, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Horizontal strip showing every registered user on the platform.
 * Click an avatar to open the user's profile. Also renders a Follow button per user.
 * Includes a live search input.
 */
export default function AllUsersStrip() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Real registered-users total, refreshed every 30s
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/stats/users-count`).then((r) => alive && setTotal(r.data.total)).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/users`, { params: { q: q || undefined, limit: 100 } });
        if (alive) setUsers(r.data || []);
      } catch { if (alive) setUsers([]); }
      setLoading(false);
    }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <Card className="p-4" data-testid="all-users-strip">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h3 className="font-semibold">Usuários cadastrados</h3>
          <span className="text-xs text-muted-foreground" data-testid="all-users-total">
            ({q ? `${users.length} encontrado${users.length === 1 ? "" : "s"}` : (total ?? users.length).toLocaleString("pt-BR")})
          </span>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome..."
            className="pl-8 h-9 text-sm"
            data-testid="all-users-search"
          />
        </div>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Carregando...</p>
      ) : users.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Nenhum usuário encontrado.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="all-users-list">
          {users.map((u) => (
            <div key={u.user_id} className="flex flex-col items-center gap-1 flex-shrink-0 w-24 text-center" data-testid={`user-chip-${u.user_id}`}>
              <Link to={`/user/${u.user_id}`} className="hover:opacity-90 transition">
                <Avatar className="w-16 h-16 border-2 border-transparent hover:border-primary transition">
                  <AvatarImage src={u.picture} alt={u.name} />
                  <AvatarFallback>{u.name?.[0] || "?"}</AvatarFallback>
                </Avatar>
              </Link>
              <p className="text-xs font-medium truncate w-full" title={u.name}>{u.name}</p>
              <FollowButton userId={u.user_id} size="sm" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
